import { ScrollArea } from "@/components/ui/scroll-area";
import { ClaimDetailed } from "@/api/generated/client/models";
import { SidebarInsuredSection } from "./sidebar/SidebarInsuredSection";
import { SidebarPropertySection } from "./sidebar/SidebarPropertySection";
import { SidebarClaimSection } from "./sidebar/SidebarClaimSection";
import { SidebarLinkedEntitiesSection } from "./sidebar/SidebarLinkedEntitiesSection";
import { SidebarAssigneeSection } from "./sidebar/SidebarAssigneeSection";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Link } from "wouter";
import { useClaimStatuses } from "@/hooks/use-statuses";
import { useClaimPipelines } from "@/hooks/use-claim-pipelines";
import { useFolders } from "@/hooks/use-folders";
import { FolderPicker } from "@/components/claims/folders/FolderPicker";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

interface ClaimInfoSidebarProps {
    claim: ClaimDetailed;
    onUpdateClaim: (data: any) => Promise<void>;
    className?: string;
}

export function ClaimInfoSidebar({
    claim,
    onUpdateClaim,
    className,
}: ClaimInfoSidebarProps) {
    const canWrite = claim.canWrite ?? false;

    const handleUpdate = async (field: string, value: any) => {
        if (!canWrite) return;
        // dateOfLoss is already in YYYY-MM-DD format, send it directly
        await onUpdateClaim({ [field]: value });
    };

    const handleStatusChange = async (statusId: string) => {
        if (!canWrite) return;
        await onUpdateClaim({ claimStatusTemplateId: parseInt(statusId) });
    };

    const { pipelines } = useClaimPipelines({ includeInactive: true });
    const { statuses } = useClaimStatuses(claim.id, claim.pipelineId);
    const currentPipeline = pipelines.find((pipeline) => pipeline.id === claim.pipelineId);
    const currentPipelineName = currentPipeline?.name ?? claim.claimPipelineName ?? null;
    const currentPipelineColor = currentPipeline?.color ?? claim.claimPipelineColor ?? "#94A3B8";
    const pipelineOptions = pipelines.filter((pipeline) => pipeline.isActive || pipeline.id === claim.pipelineId);
    const canChangePipeline = canWrite && !!currentPipeline && pipelineOptions.length > 0;
    const currentStatus = statuses.find((status) => status.id === claim.claimStatusTemplateId);
    const { folders, manageableFolders } = useFolders();

    return (
        <div className={cn("w-[25%] bg-muted/40 border-r flex flex-col min-w-[300px] max-w-[400px] max-sm:h-full max-sm:min-h-0", className)}>
            <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
                <div className="p-4 space-y-4">
                    {/* Back button at the top */}
                    <Link href="/claims">
                        <Button variant="ghost" size="sm" className="w-full justify-start gap-2 -ml-2">
                            <ChevronLeft className="h-4 w-4" />
                            Back to Claims
                        </Button>
                    </Link>

                    <Card className="bg-card p-3 shadow-xs">
                        <div className="grid grid-cols-2 gap-2">
                            <div className="min-w-0 space-y-1">
                                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Workflow</span>
                                {canChangePipeline ? (
                                    <Select
                                        value={claim.pipelineId?.toString() || undefined}
                                        onValueChange={(pipelineId) => onUpdateClaim({ pipelineId: parseInt(pipelineId) })}
                                    >
                                        <SelectTrigger className="h-8 w-full min-w-0 px-2 text-xs">
                                            <SelectValue placeholder="Select workflow">
                                                {currentPipelineName && (
                                                    <span className="inline-flex min-w-0 items-center gap-1.5">
                                                        <span
                                                            className="h-2 w-2 shrink-0 rounded-full border"
                                                            style={{ backgroundColor: currentPipelineColor }}
                                                        />
                                                        <span className="truncate">{currentPipelineName}</span>
                                                    </span>
                                                )}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent side="bottom" collisionPadding={8}>
                                            {pipelineOptions.map((pipeline) => (
                                                <SelectItem key={pipeline.id} value={pipeline.id.toString()}>
                                                    <span className="inline-flex items-center gap-2">
                                                        <span
                                                            className="h-2.5 w-2.5 rounded-full border"
                                                            style={{ backgroundColor: pipeline.color ?? "#94A3B8" }}
                                                        />
                                                        {pipeline.name}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Badge
                                        variant="outline"
                                        className="h-8 w-full min-w-0 justify-start truncate rounded-md px-2 py-0 text-xs font-medium"
                                        style={{ borderColor: currentPipelineColor }}
                                        title={currentPipelineName ?? "No Workflow"}
                                    >
                                        {currentPipelineName ?? "No Workflow"}
                                    </Badge>
                                )}
                            </div>

                            <div className="min-w-0 space-y-1">
                                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Status</span>
                                <Select
                                    value={claim.claimStatusTemplateId?.toString() || undefined}
                                    onValueChange={handleStatusChange}
                                    disabled={!canWrite}
                                >
                                    <SelectTrigger className="h-8 w-full min-w-0 px-2 text-xs">
                                        <SelectValue placeholder="Select status">
                                            {currentStatus && (
                                                <span className="inline-flex min-w-0 items-center gap-1.5">
                                                    <span
                                                        className="h-2 w-2 shrink-0 rounded-full border"
                                                        style={{ backgroundColor: currentStatus.color ?? "#94A3B8" }}
                                                    />
                                                    <span className="truncate">{currentStatus.displayName}</span>
                                                </span>
                                            )}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent side="bottom" collisionPadding={8}>
                                        {statuses.map((status) => (
                                            <SelectItem key={status.id} value={status.id.toString()}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className="h-2.5 w-2.5 rounded-full border"
                                                        style={{ backgroundColor: status.color ?? "#94A3B8" }}
                                                    />
                                                    {status.displayName}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {folders.length > 0 && (
                            <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2">
                                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Folder</span>
                                <FolderPicker
                                    folders={folders}
                                    assignableFolders={manageableFolders}
                                    value={claim.claimFolderId}
                                    disabled={!canWrite}
                                    onChange={(folderId) => canWrite && onUpdateClaim({ claimFolderId: folderId })}
                                />
                            </div>
                        )}
                    </Card>
                    
                    <SidebarInsuredSection claim={claim} onUpdate={handleUpdate} canWrite={canWrite} />
                    <SidebarPropertySection claim={claim} onUpdate={handleUpdate} canWrite={canWrite} />
                    <SidebarClaimSection 
                        claim={claim} 
                        onUpdate={handleUpdate}
                        canWrite={canWrite}
                    />
                    <SidebarLinkedEntitiesSection claim={claim} />
                    <SidebarAssigneeSection claim={claim} />
                </div>
            </ScrollArea>
        </div>
    );
}
